import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Single horizontal product carousel. Planes × FTA first, FTA Fall second.
// Auto-advances slowly, pauses on hover, manual arrows/swipe.
export default function MerchSection() {
  const API = process.env.REACT_APP_BACKEND_URL;
  const [products, setProducts] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const scroller = useRef(null);
  const touchX = useRef(null);

  useEffect(() => {
    fetch(`${API}/api/products`).then(r => r.json()).then((all) => {
      const order = { "planes-fta": 0, "fta-fall": 1 };
      const sorted = [...all].sort((a, b) => (order[a.collection] ?? 9) - (order[b.collection] ?? 9));
      setProducts(sorted);
    });
  }, [API]);

  useEffect(() => {
    if (paused || products.length < 2) return;
    const t = setInterval(() => setIdx(i => (i + 1) % products.length), 4200);
    return () => clearInterval(t);
  }, [paused, products.length]);

  // Programmatically scroll ONLY the horizontal track — never the viewport.
  // Using scrollIntoView({block:'nearest'}) previously pulled the whole page
  // upward on each autoplay tick. Fix: manipulate scroller.scrollLeft directly.
  useEffect(() => {
    if (!scroller.current) return;
    const child = scroller.current.children[idx];
    if (!child) return;
    const target = child.offsetLeft - (scroller.current.clientWidth - child.clientWidth) / 2;
    scroller.current.scrollTo({ left: target, behavior: "smooth" });
  }, [idx]);

  const nudge = (d) => setIdx(i => (i + d + products.length) % products.length);

  return (
    <section id="merch" data-testid="merch-section" className="relative bg-white text-black py-16 md:py-24">
      <div className="px-4 md:px-10 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between mb-8 md:mb-10">
          <div>
            <div className="font-mono text-[10px] tracking-[0.4em] uppercase text-[#B48A00] mb-2">New Arrivals</div>
            <h2 data-testid="merch-heading" className="font-wide uppercase text-black text-4xl sm:text-5xl">Shop the Collection</h2>
          </div>
          <div className="hidden md:flex gap-2">
            <button data-testid="carousel-prev" aria-label="Previous" onClick={() => nudge(-1)} className="border border-black/20 hover:bg-black hover:text-white p-2"><ChevronLeft className="w-5 h-5"/></button>
            <button data-testid="carousel-next" aria-label="Next" onClick={() => nudge(1)} className="border border-black/20 hover:bg-black hover:text-white p-2"><ChevronRight className="w-5 h-5"/></button>
          </div>
        </div>
      </div>

      <div className="relative"
        onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => { if (touchX.current == null) return; const dx = e.changedTouches[0].clientX - touchX.current; if (Math.abs(dx) > 40) nudge(dx < 0 ? 1 : -1); touchX.current = null; }}>
        <div ref={scroller} data-testid="carousel-track"
          className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth px-4 md:px-10 pb-6"
          style={{ scrollbarWidth: "none" }}>
          {products.map((p) => (
            <Link key={p.slug} to={`/product/${p.slug}`} data-testid={`product-card-${p.slug}`}
              className="snap-center flex-shrink-0 w-[70vw] sm:w-[46vw] md:w-[30vw] lg:w-[22vw] group">
              <div className="relative aspect-square bg-neutral-100 overflow-hidden">
                <img src={p.images[0]} alt={p.name} loading="lazy"
                  className="w-full h-full object-contain p-6 transition-transform duration-500 group-hover:scale-105" />
                {p.is_new && <span className="absolute top-3 left-3 bg-black text-white text-[10px] font-mono tracking-[0.25em] px-2 py-1">NEW</span>}
              </div>
              <div className="mt-3">
                <div className="font-display uppercase text-sm md:text-base leading-tight line-clamp-2">{p.name}</div>
                <div className="mt-1 font-mono text-xs md:text-sm">${p.price.toFixed(2)}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
